import FastModal from 'components/FastModal';
import {FmlxCheckBox, FmlxIcon, FmlxSwitch} from 'fmlx-common-ui';
import {useState} from 'react';
import {useIntl} from 'react-intl';
import {useDispatch, useSelector} from 'react-redux';
import {hideTypedModal, showModalType} from 'stores/actions/typedModalAction';
import {
  selectIsPlateRecognitionEnabled,
  selectIsAlreadyScannedAllEmptyNests,
  selectIsIncompatibleVisionVersion,
} from 'stores/selectors/plateRecognitionSelectors';
import {setPlateRecognitionState} from 'stores/business/plateRecognitionBusiness';

import './PlateRecognitionModal.scss';
import {Feedback, ModalType} from 'constants/dataEnum';
import {getInstrumentProcessStatus} from 'api/instrumentManagementApi';
import {InstrumentProcessType} from 'constants/instrumentStatusEnum';
import {showIsBusyModal} from 'stores/business/commonModalBusiness';
import {showSnackbar} from 'stores/actions/appStateAction';

function ModalPlateRecognition() {
  const dispatch = useDispatch();
  const {formatMessage} = useIntl();

  const isAlreadyScannedAllEmptyNests = useSelector(selectIsAlreadyScannedAllEmptyNests);
  const isFirstUse = !isAlreadyScannedAllEmptyNests;
  const enabled = useSelector(selectIsPlateRecognitionEnabled);
  const isIncompatibleVisionVersion = useSelector(selectIsIncompatibleVisionVersion);

  const [switchStatus, setSwitchStatus] = useState(enabled);
  const [isTrayInstalledChecked, setIsTrayInstalledChecked] = useState(false);

  const handleSwitchChange = () => {
    const newStatus = !switchStatus;
    setSwitchStatus(newStatus);
  };

  const handleCheckboxChange = () => {
    const newStatus = !isTrayInstalledChecked;
    setIsTrayInstalledChecked(newStatus);
  };

  const handleCloseModal = () => {
    dispatch(hideTypedModal());
  };

  const handleProceed = async () => {
    if (isIncompatibleVisionVersion) {
      dispatch(showModalType(ModalType.INCOMPATIBLE_VISION_VERSION));
      return;
    }

    const {status} = (await getInstrumentProcessStatus()) || {};
    if (status === InstrumentProcessType.PROTOCOL_EXECUTION || status === InstrumentProcessType.PLATE_DEFINITION) {
      if (switchStatus) {
        dispatch(showIsBusyModal());
      } else {
        dispatch(
          showSnackbar(
            Feedback.WARNING,
            formatMessage({
              id: 'modal.plateRecognition.toast.busy',
            }),
          ),
        );
      }
      return;
    }

    await dispatch(setPlateRecognitionState(switchStatus));
    handleCloseModal();
  };

  const content = (
    <div className="plate-recognition-modal">
      <div className="plate-recognition-header">
        <span>{formatMessage({id: 'modal.plateRecognition.title'})}</span>
        <FmlxSwitch size={FmlxSwitch.Size.SMALL} checked={switchStatus} onChange={handleSwitchChange} />
      </div>
      <div>
        {enabled ? (
          <p>{formatMessage({id: 'modal.plateRecognition.description.disable'})}</p>
        ) : (
          <p>{formatMessage({id: 'modal.plateRecognition.description.enable'})}</p>
        )}
      </div>
      {isFirstUse && (
        <div className="plate-recognition-first-use">
          <div>
            <FmlxIcon name="Info" size={FmlxIcon.Size.SMALL} customColor="#006AF5" className="humidity-switch__icon" />
          </div>
          <div className="first-use-content">
            <span>{formatMessage({id: 'modal.plateRecognition.firstUse'})}</span>
            <FmlxCheckBox
              label={formatMessage({id: 'modal.plateRecognition.trayInstalled.label'})}
              size="sm"
              checked={isTrayInstalledChecked}
              onChange={handleCheckboxChange}
            />
          </div>
        </div>
      )}
    </div>
  );

  const buttons = [
    {
      style: 'primary',
      text: formatMessage({id: 'modal.plateRecognition.firstButton'}),
      handlerClick: handleProceed,
      disabled: switchStatus === enabled || (isFirstUse && !isTrayInstalledChecked),
    },
    {
      style: 'secondary',
      text: formatMessage({id: 'modal.plateRecognition.secondButton'}),
      handlerClick: handleCloseModal,
    },
  ];

  return (
    <FastModal
      show
      header={formatMessage({id: 'modal.plateRecognition.title'})}
      content={content}
      btnAction={buttons}
      onClose={handleCloseModal}
      closeDisabled={false}
    />
  );
}

export default ModalPlateRecognition;
